import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { inspectionTemplateService } from '../../../services/inspectionTemplateService';

const CATEGORY_LABELS = { frenos: 'Frenos', suspension: 'Suspensión', motor: 'Motor', otros: 'Otros Sistemas', llantas: 'Llantas' };
const STATUS_COLORS = {
  bueno: 'bg-success/10 text-success border-success/20',
  regular: 'bg-warning/10 text-warning border-warning/20',
  malo: 'bg-error/10 text-error border-error/20',
  critico: 'bg-destructive/10 text-destructive border-destructive/20'
};
const STATUS_LABELS = { bueno: 'Bueno', regular: 'Regular', malo: 'Malo', critico: 'Crítico' };

const InspectionTemplatePanel = ({ category, companyId, onApplyTemplate, isVisible, onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState(null);

  useEffect(() => {
    if (isVisible && category) loadTemplates();
  }, [isVisible, category]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await inspectionTemplateService?.getByCategory(category, companyId);
      setTemplates(data || []);
    } catch (err) {
      console.error('[InspectionTemplatePanel] Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = (template) => {
    setApplied(template?.id);
    onApplyTemplate?.({
      status: template?.conditionStatus,
      findings: template?.findingsText,
      recommendations: template?.recommendationsText,
      parts: template?.partsJson || [],
      labor: template?.laborJson || []
    });
    setTimeout(() => { setApplied(null); onClose?.(); }, 600);
  };

  if (!isVisible) return null;

  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-elevation-4 z-20 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <Icon name="Zap" size={16} className="text-primary" />
          <span className="text-sm font-heading font-semibold text-foreground">
            Plantillas rápidas — {CATEGORY_LABELS?.[category] || category}
          </span>
          <span className="text-xs caption text-muted-foreground">({templates?.length} disponibles)</span>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-muted transition-smooth" aria-label="Cerrar plantillas">
          <Icon name="X" size={16} className="text-muted-foreground" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 gap-2">
          <Icon name="Loader2" size={18} className="text-primary animate-spin" />
          <span className="text-sm text-muted-foreground">Cargando plantillas...</span>
        </div>
      ) : templates?.length === 0 ? (
        <div className="py-8 text-center">
          <Icon name="FileX" size={32} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No hay plantillas disponibles</p>
        </div>
      ) : (
        <div className="max-h-80 overflow-y-auto divide-y divide-border">
          {templates?.map((template) => (
            <button
              key={template?.id}
              onClick={() => handleApply(template)}
              className={`w-full text-left px-4 py-3 hover:bg-muted/50 transition-smooth press-effect focus-ring group ${applied === template?.id ? 'bg-success/10' : ''}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-body font-semibold text-foreground truncate">{template?.templateName}</span>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${STATUS_COLORS?.[template?.conditionStatus] || 'bg-muted text-muted-foreground border-border'}`}>
                      {STATUS_LABELS?.[template?.conditionStatus] || template?.conditionStatus}
                    </span>
                  </div>
                  <p className="text-xs caption text-muted-foreground line-clamp-2">{template?.findingsText}</p>
                  {template?.partsJson?.length > 0 && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <Icon name="Package" size={12} className="text-muted-foreground" />
                      <span className="text-xs caption text-muted-foreground">
                        {template?.partsJson?.length} pieza{template?.partsJson?.length !== 1 ? 's' : ''} sugerida{template?.partsJson?.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
                <div className={`flex-shrink-0 p-1.5 rounded-lg transition-smooth ${applied === template?.id ? 'bg-success text-success-foreground' : 'bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground'}`}>
                  <Icon name={applied === template?.id ? 'Check' : 'Zap'} size={14} />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      <div className="px-4 py-2 border-t border-border bg-muted/20">
        <p className="text-xs caption text-muted-foreground">
          <Icon name="Info" size={12} className="inline mr-1" />
          Al aplicar una plantilla puedes editar los campos antes de guardar
        </p>
      </div>
    </div>
  );
};

export default InspectionTemplatePanel;
